import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import QuoteButton from "./QuoteButton";
import { CONTACT } from "../../lib/site";
import { gaEvent } from "../../lib/analytics";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/doors", label: "Doors" },
  { to: "/windows", label: "Windows" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || !isHome || open;
  const linkColor = solid ? "text-ink" : "text-cream";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-cream/90 backdrop-blur-xl border-b border-gold/15 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-10">
        <Link to="/" aria-label="Eminence Windows & Doors home">
          <Logo dark={solid} size="sm" />
        </Link>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link text-sm font-medium tracking-wide transition-colors ${linkColor} hover:text-gold`
              }
            >
              {({ isActive }) => (
                <span data-active={isActive}>{l.label}</span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-phone"
            onClick={() => gaEvent("phone_click", { link_location: "navbar" })}
            className={`hidden items-center gap-2 text-sm font-medium md:flex ${linkColor} hover:text-gold transition-colors`}
          >
            <Phone className="h-4 w-4 text-gold" />
            {CONTACT.phone}
          </a>
          <div className="hidden sm:block">
            <QuoteButton size="sm" testId="nav-request-quote-btn" />
          </div>
          <button
            className={`lg:hidden ${linkColor}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          className="lg:hidden border-t border-gold/15 bg-cream px-5 pb-6 pt-2"
          data-testid="mobile-menu"
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className="block border-b border-gold/10 py-3 font-serif text-2xl text-ink"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-5 flex flex-col gap-3">
            <a
              href={CONTACT.phoneHref}
              onClick={() => gaEvent("phone_click", { link_location: "mobile_menu" })}
              className="flex items-center gap-2 text-sm font-medium text-ink"
            >
              <Phone className="h-4 w-4 text-gold" /> {CONTACT.phone}
            </a>
            <QuoteButton size="md" testId="mobile-request-quote-btn" />
          </div>
        </div>
      )}
    </header>
  );
}
